import { useEffect, useState, useCallback } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AdminMessageUserDialog from "@/components/AdminMessageUserDialog";
import {
  ArrowLeft, Star, ShieldCheck, Award, ExternalLink, MapPin, Calendar,
  Store, Users2, CheckCircle2, XCircle, DollarSign, Mail,
} from "lucide-react";

// Admin gate: trust backend `is_admin` (source of truth = ADMIN_EMAIL env).

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch { return iso; }
}

function StatusPill({ children, tone = "neutral" }) {
  const map = {
    ok: { bg: "rgba(61,111,66,0.15)", fg: "var(--moss)" },
    warn: { bg: "rgba(201,162,75,0.15)", fg: "#8a6a2b" },
    danger: { bg: "rgba(200,107,74,0.15)", fg: "var(--terracotta)" },
    neutral: { bg: "rgba(0,0,0,0.06)", fg: "var(--ink-soft)" },
  };
  const c = map[tone] || map.neutral;
  return (
    <span
      className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
      style={{ backgroundColor: c.bg, color: c.fg }}
    >
      {children}
    </span>
  );
}

function InfoRow({ label, value, testId }) {
  return (
    <div className="flex gap-4 py-2 border-b" style={{ borderColor: "rgba(0,0,0,0.05)" }}>
      <div
        className="w-44 text-[10px] uppercase tracking-widest shrink-0"
        style={{ color: "var(--ink-soft)" }}
      >
        {label}
      </div>
      <div className="flex-1 min-w-0 text-sm" data-testid={testId}>
        {value || <span style={{ color: "var(--ink-soft)" }}>—</span>}
      </div>
    </div>
  );
}

export default function AdminUserDetail() {
  const { user, loading } = useAuth();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  // Message dialog target — populated when the admin taps "Message" on
  // this user's detail page. Shares the same dialog component used from
  // the /admin/users list so the conversation history flows through.
  const [messageOpen, setMessageOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setData(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, [id]);

  useEffect(() => {
    if (user && user.is_admin) load();
  }, [user, load]);

  if (loading) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const verifyOrganizer = async () => {
    if (!data) return;
    const willVerify = !data.user.is_verified;
    if (!window.confirm(`${willVerify ? "Verify" : "Un-verify"} ${data.user.name || data.user.email}?`)) return;
    try {
      await api.post(`/admin/organizers/${data.user.id}/verify`, { verified: willVerify });
      toast.success(`${willVerify ? "Verified" : "Un-verified"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14" data-testid="admin-user-detail-page">
      <Link to="/admin/users" className="text-sm inline-flex items-center gap-1.5 underline" style={{ color: "var(--ink-soft)" }}>
        <ArrowLeft size={14} /> Back to directory
      </Link>

      {busy && !data && (
        <div className="mt-10 text-sm" style={{ color: "var(--ink-soft)" }} data-testid="user-detail-loading">Loading…</div>
      )}
      {err && (
        <div
          data-testid="user-detail-error"
          className="mt-6 text-sm rounded-md p-3"
          style={{ backgroundColor: "rgba(200,107,74,0.08)", color: "var(--terracotta)" }}
        >
          {err}
        </div>
      )}

      {data && (
        <>
          <div className="mt-6 flex items-start gap-5 flex-wrap">
            {data.user.avatar ? (
              <img
                src={data.user.avatar}
                alt=""
                className="w-20 h-20 rounded-full object-cover shrink-0"
                style={{ backgroundColor: "var(--sand)" }}
              />
            ) : (
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center shrink-0 font-serif text-3xl"
                style={{ backgroundColor: "var(--sand)", color: "var(--terracotta)" }}
              >
                {(data.user.name || data.user.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="overline" style={{ color: "var(--terracotta)" }}>
                  {data.user.role === "organizer" ? <>Organizer</> : <>Vendor</>}
                </div>
                {data.user.is_verified && (
                  <StatusPill tone="ok"><ShieldCheck size={10} /> Verified</StatusPill>
                )}
                {data.user.plan === "pro" && (
                  <StatusPill tone="warn"><Star size={10} /> Maker Pro</StatusPill>
                )}
                {data.user.suspended && <StatusPill tone="danger"><XCircle size={10} /> Suspended</StatusPill>}
              </div>
              <h1 className="font-serif text-4xl mt-1 leading-tight">{data.user.name || <em>no name</em>}</h1>
              <div className="mt-2 text-sm inline-flex items-center gap-2" style={{ color: "var(--ink-soft)" }}>
                <Mail size={14} /> {data.user.email}
              </div>
              {data.user.business_name && (
                <div className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>{data.user.business_name}</div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 items-start">
              <Button
                data-testid="user-message-btn"
                onClick={() => setMessageOpen(true)}
                className="rounded-full text-sm inline-flex items-center gap-1"
                style={{ backgroundColor: "var(--terracotta)", color: "#fff" }}
              >
                <Mail size={14} /> Message
              </Button>
              {data.user.handle && data.user.role === "vendor" && (
                <a
                  href={`/@${data.user.handle}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="user-view-public"
                  className="btn-outline rounded-full px-4 py-2 text-sm inline-flex items-center gap-2"
                >
                  <ExternalLink size={14} /> Public profile
                </a>
              )}
              {data.user.role === "organizer" && (
                <Button
                  data-testid="user-verify-btn"
                  variant="outline"
                  onClick={verifyOrganizer}
                  className="rounded-full text-sm inline-flex items-center gap-1"
                >
                  <Award size={14} /> {data.user.is_verified ? "Un-verify" : "Verify"}
                </Button>
              )}
            </div>
          </div>

          {data.user.bio && (
            <p className="mt-6 text-sm max-w-3xl" style={{ color: "var(--ink-soft)" }}>{data.user.bio}</p>
          )}

          {/* KPI strip */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat icon={data.user.role === "organizer" ? Calendar : Store}
                  label={data.user.role === "organizer" ? "Events posted" : "Applications"}
                  value={data.user.role === "organizer" ? data.events.length : data.applications.length} />
            <Stat icon={Star} label="Ratings" value={data.ratings.length}
                  hint={data.ratings.length > 0 ? `${data.avg_rating.toFixed(1)}★ avg` : null} />
            <Stat icon={Users2} label="Joined" value={fmtDate(data.user.created_at)} />
            <Stat icon={CheckCircle2} label="Last login" value={fmtDate(data.user.last_login_at)} />
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-8">
            {/* Account details */}
            <div>
              <div className="overline mb-3" style={{ color: "var(--ink-soft)" }}>Account details</div>
              <div className="card-warm rounded-sm p-6">
                <InfoRow label="ID" value={<span className="font-mono text-[11px]">{data.user.id}</span>} />
                <InfoRow label="Handle" value={data.user.handle ? `@${data.user.handle}` : ""} />
                <InfoRow label="City / State" value={[data.user.city, data.user.state].filter(Boolean).join(", ")} />
                <InfoRow label="Plan" value={<>{data.user.plan === "pro" ? "Maker Pro" : "Free"}{data.user.plan_interval ? ` · ${data.user.plan_interval}` : ""}{data.user.plan_status ? ` · ${data.user.plan_status}` : ""}</>} />
                <InfoRow label="Stripe customer" value={data.user.stripe_customer_id ? <span className="font-mono text-[11px] break-all">{data.user.stripe_customer_id}</span> : ""} />
                <InfoRow label="Stripe subscription" value={data.user.stripe_subscription_id ? <span className="font-mono text-[11px] break-all">{data.user.stripe_subscription_id}</span> : ""} />
                {data.user.role === "organizer" && (
                  <>
                    <InfoRow label="Stripe Connect ID" value={data.user.stripe_connect_account_id ? <span className="font-mono text-[11px] break-all">{data.user.stripe_connect_account_id}</span> : ""} />
                    <InfoRow label="Connect charges" value={
                      data.user.stripe_connect_charges_enabled
                        ? <StatusPill tone="ok">Enabled</StatusPill>
                        : <StatusPill tone="danger">Disabled</StatusPill>
                    } />
                  </>
                )}
                <InfoRow label="Categories" value={(data.user.categories || []).join(", ")} />
                <InfoRow label="Email opt-in" value={data.user.email_opt_in ? "Yes" : "No"} />
              </div>
            </div>

            {/* Activity */}
            <div>
              {data.user.role === "organizer" ? (
                <>
                  <div className="overline mb-3" style={{ color: "var(--ink-soft)" }}>Events · {data.events.length}</div>
                  <div className="card-warm rounded-sm p-6">
                    {data.events.length === 0 ? (
                      <div className="text-sm py-3" style={{ color: "var(--ink-soft)" }}>No events posted yet.</div>
                    ) : (
                      <ul className="divide-y" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                        {data.events.map((e) => (
                          <li key={e.id} className="py-3">
                            <Link to={`/events/${e.id}`} target="_blank" rel="noreferrer" className="hover:underline">
                              <div className="flex items-baseline justify-between gap-3">
                                <div className="font-medium truncate">{e.title}</div>
                                <StatusPill tone={e.moderation_status === "live" ? "ok" : "warn"}>{e.moderation_status}</StatusPill>
                              </div>
                              <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>
                                <MapPin size={11} className="inline mr-1" />
                                {e.city} · {fmtDate(e.date)} · <DollarSign size={11} className="inline" />{e.booth_fee.toFixed(2)} · max {e.max_vendors}
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="overline mb-3" style={{ color: "var(--ink-soft)" }}>Applications · {data.applications.length}</div>
                  <div className="card-warm rounded-sm p-6">
                    {data.applications.length === 0 ? (
                      <div className="text-sm py-3" style={{ color: "var(--ink-soft)" }}>No applications yet.</div>
                    ) : (
                      <ul className="divide-y" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                        {data.applications.map((a) => (
                          <li key={a.id} className="py-3">
                            <Link to={`/events/${a.event_id}`} target="_blank" rel="noreferrer" className="hover:underline">
                              <div className="flex items-baseline justify-between gap-3">
                                <div className="font-medium truncate">{a.event_title}</div>
                                <StatusPill tone={a.status === "approved" ? "ok" : a.status === "rejected" ? "danger" : "warn"}>{a.status}</StatusPill>
                              </div>
                              <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>
                                {a.event_city} · {fmtDate(a.event_date)} · Payment: {a.payment_status || "—"}
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Ratings */}
          <div className="mt-10">
            <div className="overline mb-3" style={{ color: "var(--ink-soft)" }}>Ratings received · {data.ratings.length}</div>
            <div className="card-warm rounded-sm p-6">
              {data.ratings.length === 0 ? (
                <div className="text-sm py-3" style={{ color: "var(--ink-soft)" }}>No ratings yet.</div>
              ) : (
                <ul className="space-y-4">
                  {data.ratings.map((r) => (
                    <li key={r.id} className="border-b pb-3 last:border-none" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                      <div className="flex items-center gap-2">
                        <Star size={12} style={{ color: "var(--gold)" }} /> <strong>{r.overall}</strong>
                        <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{r.direction}</span>
                        <span className="text-xs ml-auto" style={{ color: "var(--ink-soft)" }}>{fmtDate(r.created_at)}</span>
                      </div>
                      {r.review && <p className="mt-1.5 text-sm">{r.review}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      <AdminMessageUserDialog
        user={data ? { id: data.user.id, name: data.user.name, email: data.user.email, business_name: data.user.business_name } : null}
        open={messageOpen}
        onOpenChange={setMessageOpen}
      />
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div
      className="rounded-lg border p-4"
      style={{ backgroundColor: "#fff", borderColor: "rgba(0,0,0,0.08)" }}
    >
      <div className="flex items-center gap-2">
        <Icon size={13} style={{ color: "var(--terracotta)" }} />
        <div className="overline" style={{ color: "var(--ink-soft)" }}>{label}</div>
      </div>
      <div className="font-serif text-2xl mt-1 leading-none">{value}</div>
      {hint && <div className="text-[11px] mt-1" style={{ color: "var(--ink-soft)" }}>{hint}</div>}
    </div>
  );
}
